/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片预览底部工具栏（下载/上传提示与操作），仅在已编辑数量 > 0 时显示
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10 10:17:00
 */

import React from 'react'
import { Button, Switch } from 'antd'
import { useWatermarkBatchEditorTrack } from '../../hooks/useWatermarkBatchEditorTrack'
import './BottomToolbar.scss'
import { CloudUploadOutlined, DownloadOutlined } from '@ant-design/icons'
import imgBatchEditorRetweet from '../../../../../images/watermark-batch-editor/batch_editor_retweet.png'

declare const __RELEASE_STAGE__: string

export interface BottomToolbarProps {
  /** 剩余编辑次数 */
  watermarkCnt: number
  /** 点击「上传到文件夹」 */
  onUploadClick: () => void
  /** 点击「下载」 */
  onDownloadClick: () => void
  /** 已处理照片数量（编辑成功 + 无修改项 + 超时） */
  processedCount: number
  /** 是否正在下载全部 */
  downloadingAll: boolean
  /** 是否显示对比编辑前（非生产环境） */
  showCompareSection?: boolean
  /** 对比开关状态 */
  compareBefore: boolean
  /** 对比开关变更 */
  onCompareChange: (checked: boolean) => void
  /** 当前照片是否有 aiURL（对比开关是否可用） */
  hasCurrentAiURL: boolean
}

const BottomToolbar: React.FC<BottomToolbarProps> = ({
  watermarkCnt,
  onUploadClick,
  onDownloadClick,
  processedCount,
  downloadingAll,
  showCompareSection = __RELEASE_STAGE__ !== 'production',
  compareBefore,
  onCompareChange,
  hasCurrentAiURL
}) => {
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  const handleUploadClick = () => {
    trackWatermarkBatchEditor({
      actionType: 'uploadToFolder',
      number1: watermarkCnt
    })
    onUploadClick()
  }

  return (
    <div className="image-preview-bottom-toolbar">
      <div className="download-section">
        <div className="toolbar-row toolbar-row--header">
          <div className="edited-title">
            已编辑照片
            <span className="edited-title-count">({processedCount})</span>
          </div>
          {showCompareSection && (
            <div className="compare-section">
              <img
                src={imgBatchEditorRetweet}
                alt="对比编辑前"
                className="compare-icon"
              />
              <span>对比编辑前</span>
              <Switch
                checked={compareBefore}
                onChange={onCompareChange}
                className="compare-switch"
                disabled={!hasCurrentAiURL}
              />
            </div>
          )}
        </div>
        <div className="toolbar-row toolbar-row--actions">
          <Button
            type="primary"
            onClick={handleUploadClick}
            className="download-btn-primary"
          >
            <CloudUploadOutlined className="download-btn-secondary-icon" />
            上传到文件夹
          </Button>

          <Button
            onClick={onDownloadClick}
            type="primary"
            loading={downloadingAll}
            disabled={downloadingAll}
            className="download-btn-secondary"
          >
            <DownloadOutlined className="download-btn-secondary-icon" />
            下载到本地
          </Button>
        </div>
        <div className="download-tip">
          下载/上传将消耗编辑次数，剩余{' '}
          <span className="download-tip-count">{watermarkCnt}</span> 次
        </div>
      </div>
    </div>
  )
}

export default BottomToolbar
